
(function() {
  console.log("Flux Checkout script starting...");

  // 🔥 EMBEDDED SETTINGS: Set at script generation time (no runtime API calls)
  const CHECKOUT_ENABLED = true;
  const CHECKOUT_APP_URL = "https://flux-checkout.vercel.app";
  const MERCHANT_ID = "m7fmf0-dw";

  // Early exit if checkout is disabled (prevents any overhead)
  if (!CHECKOUT_ENABLED) {
    console.log("Flux: Checkout disabled for this merchant");
    return;
  }

  // Cart retry constants
  const CART_RETRY_DELAY = 300; // Base delay for cart retries
  const CART_MAX_RETRIES = 5; // Max cart fetch attempts
  const CART_WAIT_AFTER_ADD = 1200; // Wait after add-to-cart for Buy It Now

  // Helper to build checkout URL with cart data
  async function getFluxCheckoutUrl(retryCount = 0) {
    try {
      // Force fresh cart data, no cache
      const response = await fetch('/cart.js', {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      const cart = await response.json();

      console.log("Flux: Cart fetched:", {
        itemCount: cart.items?.length,
        hasToken: !!cart.token,
        firstItem: cart.items?.[0]?.variant_id,
        attempt: retryCount + 1
      });

      // If cart is empty and we haven't retried yet, try again with exponential backoff
      if ((!cart.items || cart.items.length === 0) && retryCount < CART_MAX_RETRIES) {
        // Exponential backoff: 300ms, 600ms, 1200ms, 2400ms, 4800ms
        const delay = CART_RETRY_DELAY * Math.pow(2, retryCount);
        console.log(`Flux: Cart empty, retrying in ${delay}ms (attempt ${retryCount + 2}/${CART_MAX_RETRIES + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return getFluxCheckoutUrl(retryCount + 1);
      }

      // Final check after retries
      if (!cart.items || cart.items.length === 0) {
        console.warn("Flux: Cart is empty after all retries");
        if (window.fluxCheckoutIntentional) {
          alert("Your cart is empty. Please add items before checking out.");
          window.fluxCheckoutIntentional = false;
        }
        return null;
      }

      const checkoutUrl = new URL(`${CHECKOUT_APP_URL}/checkout/${MERCHANT_ID}`);

      const items = cart.items.map(item => `${item.variant_id}:${item.quantity}`).join(',');
      checkoutUrl.searchParams.append('items', items);
      checkoutUrl.searchParams.append('currency', cart.currency || 'UAH');

      // Pass cart token/ID for clearing after successful payment
      if (cart.token) {
        checkoutUrl.searchParams.append('cart_id', cart.token);
      }

      // Capture cart note from the form (universal approach for any Shopify theme)
      const noteField = document.querySelector('textarea[name="note"]') ||
                       document.querySelector('input[name="note"]') ||
                       document.querySelector('[name="note"]');

      if (noteField && noteField.value && noteField.value.trim()) {
        console.log("Flux: Found cart note:", noteField.value.trim());
        checkoutUrl.searchParams.append('note', noteField.value.trim());
      } else {
        console.log("Flux: No cart note found");
      }

      console.log("Flux: Built checkout URL with items:", items);
      return checkoutUrl.toString();
    } catch (error) {
      console.error("Flux: Cart fetch failed:", error);
      if (window.fluxCheckoutIntentional && retryCount >= 2) {
        alert("Failed to load cart. Please refresh the page and try again.");
        window.fluxCheckoutIntentional = false;
      }
      return null;
    }
  }

  // Selectors that identify cart-count badge elements across themes.
  const FLUX_CART_COUNT_SELECTORS = [
    '.cart-count-bubble', '.cart-count', '.cart-item-count', '.cart-item-count-bubble',
    '#cart-icon-bubble .cart-count-bubble', '[data-cart-count]', '.cart-link__bubble',
    '.header__cart-count', '.site-header__cart-count', '.js-cart-count', '.cart-qty',
    '.cart_count', '.cart-counter', '.cart-count-badge'
  ];
  // Selectors that identify cart markup (drawer / cart-page item list) across themes.
  // Used to detect which page sections need re-rendering after the cart is emptied.
  const FLUX_CART_MARKER_SELECTORS = FLUX_CART_COUNT_SELECTORS.concat([
    'cart-drawer', '#CartDrawer', '.cart-drawer', '#cart-drawer', '.drawer--cart',
    '.mini-cart', '.minicart', '.ajaxcart', '#cart-icon-bubble',
    'cart-items', '#main-cart-items', '.cart-items', '.cart__items', '#cart-items',
    '[data-cart-items]'
  ]);

  // Set every cart-count badge to "0" synchronously. NEVER hide/remove the element:
  // many themes keep the cart icon's badge permanently visible (showing "0" when empty),
  // so hiding it makes the whole cart icon vanish until the next reload.
  function setCartCountToZero() {
    try {
      document.querySelectorAll(FLUX_CART_COUNT_SELECTORS.join(',')).forEach(function(el) {
        el.textContent = '0';
      });
    } catch (e) { /* ignore */ }
  }

  // Replace a stale section element with freshly-rendered HTML from the Section
  // Rendering API. The response may or may not include the .shopify-section wrapper,
  // so handle both shapes.
  function fluxSwapSection(el, html, sectionId) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrapper = doc.getElementById('shopify-section-' + sectionId) ||
        doc.querySelector('.shopify-section');
      if (wrapper) {
        el.outerHTML = wrapper.outerHTML;
      } else {
        el.innerHTML = html;
      }
    } catch (e) {
      try { el.innerHTML = html; } catch (e2) { /* ignore */ }
    }
  }

  // Re-render the cart drawer / cart-page sections WITHOUT a page reload, so the visible
  // item list empties immediately instead of lingering until a second reload. Shopify
  // wraps every section in <div id="shopify-section-{id}">; we find the cart-related ones
  // present on THIS page and ask the Section Rendering API (in the context of the current
  // URL, where these sections are guaranteed to exist) for their fresh, now-empty markup.
  function rerenderCartSections() {
    try {
      const targets = {};
      document.querySelectorAll('[id^="shopify-section-"]').forEach(function(sec) {
        if (sec.querySelector(FLUX_CART_MARKER_SELECTORS.join(','))) {
          targets[sec.id.replace('shopify-section-', '')] = sec;
        }
      });
      const sectionIds = Object.keys(targets);
      if (sectionIds.length === 0) return; // unusual theme — events below + next nav handle it

      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionIds.join(','));
      fetch(url.toString(), { cache: 'no-cache' })
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(data) {
          if (!data) return;
          sectionIds.forEach(function(id) {
            if (typeof data[id] === 'string' && targets[id]) {
              fluxSwapSection(targets[id], data[id], id);
            }
          });
          setCartCountToZero(); // re-assert after swap in case markup changed
        })
        .catch(function() { /* leave UI to the cart-update events / next navigation */ });
    } catch (e) { /* ignore */ }
  }

  // Reflect an emptied cart in the theme UI WITHOUT reloading the page:
  //  1. Set the count badge to "0" synchronously — instant, works on every theme.
  //  2. Re-render the cart drawer / cart-page sections via the Section Rendering API so
  //     the item list empties in place (the badge alone was previously updated, leaving
  //     the drawer showing stale items until a second reload).
  //  3. Dispatch the cart-update events for themes with their own custom listeners.
  // Every step is best-effort and strictly non-destructive — on an unusual theme the cart
  // just refreshes on the next navigation, never a reload, and nothing can break.
  function refreshCartUI() {
    try {
      setCartCountToZero();
      rerenderCartSections();
      ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function(name) {
        try {
          document.dispatchEvent(new CustomEvent(name, {
            bubbles: true,
            detail: { cart: { item_count: 0, items: [] } }
          }));
        } catch (e) { /* ignore */ }
      });
    } catch (e) { /* never break the storefront */ }
  }

  // Auto-clear a stale Online Store cart after a completed order.
  // The theme drawer reads the Online Store cart (cookie-based, /cart.js). A
  // server-side Storefront API clear cannot empty it, so we clear it here in the
  // customer's browser via /cart/clear.js (same-origin) once our app confirms this
  // cart token already produced a completed/paid order. Runs on every store page
  // load; fully fail-safe — any problem just leaves the cart untouched.
  async function clearStoreCartIfOrderCompleted() {
    try {
      // LEGACY/BACK-COMPAT: current back-to-store links are clean (no query flag) and
      // rely on the /api/cart/completed check below. This only fires for an old or
      // bookmarked link that still carries ?flux_clear_cart=1 — clear immediately and
      // strip the flag from the address bar. Harmless to keep; never set by new links.
      const here = new URL(window.location.href);
      if (here.searchParams.get('flux_clear_cart')) {
        here.searchParams.delete('flux_clear_cart');
        try {
          await fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
        } catch (e) { /* ignore — the fallback below catches it on the next load */ }
        // Update the cart UI in place (no reload) and strip the flag from the address
        // bar via replaceState (no navigation, no history entry, no clear loop).
        refreshCartUI();
        try { window.history.replaceState({}, '', here.toString()); } catch (e) { /* ignore */ }
        return;
      }

      const cartRes = await fetch('/cart.js', { cache: 'no-cache' });
      if (!cartRes.ok) return;
      const cart = await cartRes.json();

      // Nothing to clear / no token to identify the cart.
      if (!cart || !cart.items || cart.items.length === 0 || !cart.token) return;

      const statusUrl = CHECKOUT_APP_URL + '/api/cart/completed?store=' +
        encodeURIComponent(MERCHANT_ID) + '&cart_id=' + encodeURIComponent(cart.token);

      const statusRes = await fetch(statusUrl, { method: 'GET' });
      if (!statusRes.ok) return; // endpoint missing/old deploy — do nothing
      const data = await statusRes.json();
      if (!data || !data.completed) return; // cart not yet converted — leave it

      // Guard per COMPLETED ORDER (not per cart token): Shopify reuses the cart token
      // after /cart/clear.js, so a token-keyed guard would block clearing a NEW order
      // placed later with the same token. Keying by order id clears each order once and
      // leaves later-added items (no new order) untouched. Persisted in localStorage so
      // it survives across sessions; also our backstop against any reload loop.
      const guardKey = 'fluxCartCleared:' + (data.orderId || cart.token);
      try {
        if (localStorage.getItem(guardKey)) return;
      } catch (e) { /* localStorage unavailable (private mode) — proceed without guard */ }

      // This cart already produced a paid/completed order → clear the stale cart.
      const clearRes = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      // If the clear didn't succeed, bail without setting the guard so the next page
      // load retries.
      if (!clearRes.ok) return;

      try { localStorage.setItem(guardKey, '1'); } catch (e) { /* ignore */ }
      console.log('Flux: cleared stale cart for completed order:', data.orderId || cart.token);

      // Reflect the now-empty cart in the theme UI without a page reload.
      refreshCartUI();
    } catch (error) {
      // Never break the storefront over cart clearing.
      console.warn('Flux: cart auto-clear skipped:', error);
    }
  }

  // Store listener references for cleanup
  let eventListenerHandler = null;
  let mutationObserver = null;

  // Re-attach listeners to dynamically added checkout buttons
  function observeDynamicContent() {
    // Feature detection: skip if MutationObserver not supported (IE10 and below)
    if (typeof MutationObserver === 'undefined') {
      console.log("Flux: MutationObserver not supported, skipping dynamic content monitoring");
      return;
    }

    // Disconnect existing observer
    if (mutationObserver) {
      mutationObserver.disconnect();
    }

    mutationObserver = new MutationObserver(function(mutations) {
      let hasNewCheckoutButtons = false;

      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === 1) { // Element node
              // Check if added node or its children have checkout buttons
              const hasCheckout = node.matches && (
                node.matches('button[name="checkout"]') ||
                node.matches('.shopify-payment-button') ||
                node.matches('form[action*="/cart"]')
              ) || (node.querySelector && (
                node.querySelector('button[name="checkout"]') ||
                node.querySelector('.shopify-payment-button') ||
                node.querySelector('form[action*="/cart"]')
              ));

              if (hasCheckout) {
                hasNewCheckoutButtons = true;
                break;
              }
            }
          }
        }
        if (hasNewCheckoutButtons) break;
      }

      if (hasNewCheckoutButtons) {
        console.log("Flux: New checkout buttons detected, listeners already attached via event delegation");
      }
    });

    // Observe entire document for dynamic content. The script runs in <head> before
    // <body> exists, so document.body can be null — fall back to documentElement (always
    // present) to avoid throwing and aborting the rest of initialization.
    const observeTarget = document.body || document.documentElement;
    if (observeTarget) {
      mutationObserver.observe(observeTarget, {
        childList: true,
        subtree: true
      });
    }
  }

  // Initialization function to allow re-running after bfcache restore
  function initFluxCheckout() {
    // Prevent duplicate initialization
    if (window.fluxCheckoutActive) {
      console.log("Flux: Already active, skipping re-initialization");
      return;
    }

    console.log("Flux: Initializing checkout interception");

    // Set flag immediately
    window.fluxCheckoutActive = true;

    // Clean up old listener if exists
    if (eventListenerHandler) {
      document.removeEventListener('click', eventListenerHandler, true);
      eventListenerHandler = null;
    }

    // Consolidated single event listener with delegation
    eventListenerHandler = async function(e) {
      // Double-submit protection
      if (window.fluxCheckoutProcessing) {
        console.log("Flux: Already processing, ignoring duplicate click");
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      const target = e.target;

      // Check for checkout button
      const isCheckoutButton = target.tagName === 'BUTTON' && target.name === 'checkout';

      // Check for Buy It Now button
      const isBuyItNow = target.tagName === 'BUTTON' && (
        target.classList.contains('shopify-payment-button__button') ||
        target.closest('.shopify-payment-button') ||
        target.closest('shopify-accelerated-checkout') ||
        target.hasAttribute('data-shopify-payment-button') ||
        target.closest('shopify-buy-it-now-button')
      );

      // Check for cart form submission
      const isCartForm = target.tagName === 'FORM' &&
        target.action.includes('/cart') && (
          target.querySelector('input[name="checkout"]') ||
          target.querySelector('button[name="checkout"]') ||
          target.querySelector('input[type="submit"][value*="heck"]') ||
          document.querySelector(`button[name="checkout"][form="${target.id}"]`)
        );

      // If not a checkout-related element, ignore
      if (!isCheckoutButton && !isBuyItNow && !isCartForm) {
        return;
      }

      console.log("Flux: Checkout action detected:", {
        isCheckoutButton,
        isBuyItNow,
        isCartForm
      });

      e.preventDefault();
      e.stopPropagation();

      // Set processing flag with try-finally
      window.fluxCheckoutProcessing = true;
      window.fluxCheckoutIntentional = true;

      try {
        // Handle Buy It Now separately (needs to add to cart first)
        if (isBuyItNow) {
          console.log("Flux: Buy It Now button detected - intercepting!");

          const productForm = target.closest('form') || document.querySelector('form[action*="/cart/add"]');

          if (productForm) {
            console.log("Flux: Found product form, adding to cart first...");
            const formData = new FormData(productForm);

            const addResponse = await fetch(productForm.action, {
              method: 'POST',
              body: formData,
              headers: {
                'X-Requested-With': 'XMLHttpRequest'
              }
            });

            if (!addResponse.ok) {
              throw new Error('Failed to add to cart');
            }

            await addResponse.json();
            console.log("Flux: Item added to cart, waiting for cart update...");

            // Wait for cart update
            await new Promise(resolve => setTimeout(resolve, CART_WAIT_AFTER_ADD));
          } else {
            console.log("Flux: No product form found, redirecting with current cart");
          }
        }

        // Get checkout URL and redirect
        const url = await getFluxCheckoutUrl();
        if (url) {
          console.log("Flux: Redirecting to:", url);
          window.location.href = url;
        } else {
          console.error("Flux: Failed to generate checkout URL");
        }
      } catch (error) {
        console.error("Flux: Checkout interception error:", error);
        if (window.fluxCheckoutIntentional) {
          alert("Failed to process checkout. Please try again.");
        }
      } finally {
        // Always reset processing flag
        window.fluxCheckoutProcessing = false;
        window.fluxCheckoutIntentional = false;
      }

      return false;
    };

    // Attach single consolidated listener
    document.addEventListener('click', eventListenerHandler, true);

    // Also intercept form submissions
    document.addEventListener('submit', function(e) {
      if (eventListenerHandler) {
        eventListenerHandler(e);
      }
    }, true);

    // Start observing dynamic content AFTER listeners are attached
    observeDynamicContent();

    console.log("Flux: Initialization complete (checkout enabled)");
  }

  // 🚀 IMMEDIATE INITIALIZATION - No waiting for DOM
  // Why this is safe:
  // - Event listeners on document work immediately (no DOM elements needed)
  // - Capture phase intercepts events before they reach buttons
  // - MutationObserver handles dynamically added buttons
  // - No querySelectorAll() at init time, so no DOM dependency
  console.log("Flux: Initializing immediately (no DOM wait)");
  // Isolate init so a failure there can't block cart auto-clear (and vice versa).
  try { initFluxCheckout(); } catch (e) { console.warn("Flux: init error:", e); }

  // Clear a stale cart if this cart already produced a completed order (fire & forget).
  clearStoreCartIfOrderCompleted();

  // Re-initialize when page is restored from bfcache (back button)
  window.addEventListener('pageshow', function(event) {
    if (event.persisted) {
      console.log("Flux: Page restored from bfcache, re-initializing");
      window.fluxCheckoutActive = false; // Reset flag to allow re-init
      window.fluxCheckoutProcessing = false; // Reset processing flag
      try { initFluxCheckout(); } catch (e) { console.warn("Flux: init error:", e); }
      // Re-check on bfcache restore — the order may have completed in another tab.
      clearStoreCartIfOrderCompleted();
    }
  });

  console.log("Flux: Ready to intercept checkout");
})();
